import { Inject, Injectable, Optional } from '@angular/core';
import { MAT_DATE_LOCALE, MatDateFormats } from '@angular/material/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { format, getHours, getMinutes, getSeconds, addSeconds } from 'date-fns';
import {
  enUS,
  enGB,
  fr,
  de,
  es,
  it,
  ja,
  nl,
  pt,
  ptBR,
  ru,
  zhCN,
  zhTW,
} from 'date-fns/locale';
import type { Locale } from 'date-fns';

/**
 * A helper service to dynamically load date-fns locale data based on browser locale
 */
@Injectable({
  providedIn: 'root',
})
export class DateFnsLocaleLoader {
  /**
   * Map of standard locale codes to date-fns locale objects
   * Default to UK format (enGB) for English unless explicitly US
   */
  private localeMap: Record<string, Locale> = {
    en: enGB, // Default English to UK format
    'en-US': enUS,
    'en-GB': enGB,
    'en-CA': enGB, // Use UK format for Canadian English
    'en-AU': enGB, // Use UK format for Australian English
    'en-NZ': enGB, // Use UK format for New Zealand English
    'en-ZA': enGB, // Use UK format for South African English
    'en-IE': enGB, // Use UK format for Irish English
    fr: fr,
    'fr-FR': fr,
    de: de,
    'de-DE': de,
    es: es,
    'es-ES': es,
    it: it,
    'it-IT': it,
    ja: ja,
    'ja-JP': ja,
    nl: nl,
    'nl-NL': nl,
    pt: pt,
    'pt-BR': ptBR,
    'pt-PT': pt,
    ru: ru,
    'ru-RU': ru,
    zh: zhCN,
    'zh-CN': zhCN,
    'zh-TW': zhTW,
  };

  /**
   * Gets the date-fns locale based on the provided locale code
   * Falls back to enGB (UK) if the locale is not supported
   */
  async loadLocale(localeCode: string): Promise<Locale> {
    try {
      // Check for explicit US format first
      if (localeCode === 'en-US' || localeCode === 'en-us') {
        return enUS;
      }

      // Extract base language if specific region isn't available
      const baseLocale = localeCode.split('-')[0];

      // Try to find exact match first, then base language, then default to UK English
      return this.localeMap[localeCode] || this.localeMap[baseLocale] || enGB;
    } catch (error) {
      console.warn(
        `Failed to load locale for ${localeCode}, falling back to UK English`,
        error
      );
      return enGB;
    }
  }
}

/**
 * Default to UK date format (dd/MM/yyyy)
 * The actual formatting logic is in the date adapter, which will handle the format
 * based on the detected locale, but defaulting to UK format
 */
export const DYNAMIC_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'dd/MM/yyyy', // Default to UK format
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'dd/MM/yyyy', // Default to UK format
    monthLabel: 'MMM yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd MMMM yyyy',
    monthYearA11yLabel: 'MMMM yyyy',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
};

/**
 * Dynamic locale date adapter that automatically detects and uses the browser's locale
 * with date-fns. This adapter includes additional time handling methods for complete date-time support.
 */
@Injectable()
export class DynamicLocaleDateAdapter extends DateFnsAdapter {
  private currentLocaleData: Locale = enUS;
  private isUKLocale = false;

  constructor(
    @Optional() @Inject(MAT_DATE_LOCALE) dateLocale: string,
    private localeLoader: DateFnsLocaleLoader
  ) {
    // Get locale from injected value or browser
    const localeCode = dateLocale || navigator.language;
    console.log('Detected locale:', localeCode);

    // Check if GB locale is needed - always default to UK format unless explicitly US
    const isExplicitlyUS = localeCode === 'en-US' || localeCode === 'en-us';

    // Always use UK format (enGB) as default, unless explicitly US
    const localeToUse = isExplicitlyUS ? enUS : enGB;

    // Call super first before accessing this
    super();
    this.setLocale(localeToUse);

    // Now we can use this
    this.isUKLocale = !isExplicitlyUS;
    this.currentLocaleData = localeToUse;

    console.log('Using UK date format:', this.isUKLocale);

    // Load the appropriate locale from our pre-defined map
    this.loadLocaleData(localeCode);
  }

  /**
   * Load the locale data synchronously
   */
  private async loadLocaleData(localeCode: string): Promise<void> {
    try {
      const localeData = await this.localeLoader.loadLocale(localeCode);
      this.setLocale(localeData);
      this.currentLocaleData = localeData;
    } catch (error) {
      console.warn(`Error setting locale ${localeCode}, using default`);
    }
  }

  /**
   * Parse a date string based on the locale format
   */
  override parse(value: any, parseFormat: any): Date | null {
    if (typeof value === 'string') {
      if (this.isUKLocale) {
        // For UK format (dd/MM/yyyy)
        const parts = value.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // 0-based months
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
      } else {
        // For US format (MM/dd/yyyy)
        const parts = value.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10) - 1; // 0-based months
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
      }

      // Try the other format as fallback if the first attempt failed
      // This helps when users manually type dates
      if (this.isUKLocale) {
        // Try US format as fallback
        const parts = value.split('/');
        if (parts.length === 3) {
          const month = parseInt(parts[0], 10) - 1; // 0-based months
          const day = parseInt(parts[1], 10);
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
      } else {
        // Try UK format as fallback
        const parts = value.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10) - 1; // 0-based months
          const year = parseInt(parts[2], 10);
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            return new Date(year, month, day);
          }
        }
      }
    }

    // Fall back to standard parsing
    return super.parse(value, parseFormat);
  }

  /**
   * Format a date as a string based on the locale
   */
  override format(date: Date, displayFormat: any): string {
    if (!this.isValid(date)) {
      return '';
    }

    // For date inputs, explicitly use UK or US format based on locale setting
    if (displayFormat === 'dateInput') {
      if (this.isUKLocale) {
        return format(date, 'dd/MM/yyyy', { locale: enGB });
      } else {
        return format(date, 'MM/dd/yyyy', { locale: enUS });
      }
    }

    // For other format tokens, use the parent implementation
    return super.format(date, displayFormat);
  }

  // Time-handling methods
  getHours(date: Date): number {
    return getHours(date);
  }

  getMinutes(date: Date): number {
    return getMinutes(date);
  }

  getSeconds(date: Date): number {
    return getSeconds(date);
  }

  addSeconds(date: Date, seconds: number): Date {
    return addSeconds(date, seconds);
  }

  parseTime(value: any): Date | null {
    // If the value is empty or null, return the current time
    if (!value) {
      const now = new Date();
      return now;
    }

    if (typeof value === 'string') {
      // Try to parse time in format HH:mm or HH:mm:ss
      const time = value.trim();

      // If empty string, return current time
      if (time === '') {
        return new Date();
      }

      // Match HH:mm or HH:mm:ss
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])(?::([0-5][0-9]))?$/;
      const match = time.match(timeRegex);

      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const seconds = match[3] ? parseInt(match[3], 10) : 0;

        const date = new Date();
        date.setHours(hours, minutes, seconds, 0);
        return date;
      }
    } else if (value instanceof Date) {
      return value;
    }

    // Default to current time if parsing fails
    return new Date();
  }

  setTime(target: Date, hours: number, minutes: number, seconds: number): Date {
    const result = new Date(target);
    result.setHours(hours, minutes, seconds);
    return result;
  }
}
